import { DateTime } from 'luxon';
import React, { useContext } from 'react';
import { TickedCheckboxPortal, UntickedCheckboxPortal } from 'src/assets/images';
import { IMerchantDetails } from 'src/common/types';
import { AcceptContractBody } from 'src/containers/self-service/business-cash-advance/calculation/accept-contract-modal/accept-contract-modal';
import { BCASubmitStatus } from 'src/containers/self-service/business-cash-advance/calculation/business-cash-advance-top-up-calculation';
import { SubmitModal } from 'src/containers/self-service/business-cash-advance/calculation/submit-modal/submit-modal';
import { FailedTopUp } from 'src/containers/self-service/business-cash-advance/failure/failed-top-up';
import { SuccessfulTopUp } from 'src/containers/self-service/business-cash-advance/success/successful-top-up';
import {
  BCALinkID,
  BCAPageName,
  BCAPageUrl,
  BCATopUpTealiumService,
} from 'src/containers/self-service/business-cash-advance/tagging/bca-top-up-tealium-service';
import HeaderNav from 'src/containers/self-service/header-nav/header-nav';
import NavigationFooter from 'src/containers/self-service/navigation-footer/navigation-footer';
import { OfferContext } from 'src/context/offer-context';
import { getContractPdfBase64, viewContractPdf } from 'src/helpers/contract';
import { Delay, controlledTimeout } from 'src/helpers/delay';
import { formatCurrencyAmountSimple } from 'src/helpers/formatting';
import useMerchantProfile from 'src/hooks/useMerchantProfile';
import { acceptBCAOffer, submitBCAOffer } from 'src/services/bca-offers.service';
import toastMessageServiceSingleton from 'src/services/toast-message.service';

enum BCATopUpStatus {
  SUCCESS = 'success',
  FAILED = 'failed',
  NONE = '',
}

function DisplayBCASuccessFail({
  status,
  retry,
  retries,
}: {
  status: BCATopUpStatus;
  retries: number;
  retry: () => void;
}) {
  return status === BCATopUpStatus.SUCCESS ? (
    <SuccessfulTopUp />
  ) : (
    <FailedTopUp retries={retries} onRetry={retry} />
  );
}

export function BusinessCashAdvanceTopUpContract() {
  const [retries, setRetries] = React.useState(0);
  const [accepted, setAccepted] = React.useState(false);
  const [bcaTopUpStatus, setBCAStatus] = React.useState(BCATopUpStatus.NONE);
  const { merchant, merchantDetails } = useMerchantProfile();
  const { selectedOffer, offers, additionalFieldValues, earlySettlementAmount } =
    useContext(OfferContext);
  const [amountOptions, setAmountOptions] = React.useState<string[] | []>([]);
  const [fundedAmountIndex, setFundedAmountIndex] = React.useState<number>(0);
  const [offerAcceptLoading, setOfferAcceptLoading] = React.useState<boolean>(false);
  const [bcaSubmitStatus, setBcaSubmitStatus] = React.useState<BCASubmitStatus>(
    BCASubmitStatus.NONE,
  );

  const viewContract = () => {
    viewContractPdf(merchantDetails, merchant, selectedOffer, true);
  };

  const submitAndAcceptOffer = async () => {
    setBCAStatus(BCATopUpStatus.NONE);
    const timeoutManager = controlledTimeout(() => {
      setBcaSubmitStatus(BCASubmitStatus.TAKING_LONG);
    }, 10000);
    setOfferAcceptLoading(true);
    setBcaSubmitStatus(BCASubmitStatus.COLLECTING_INFO);

    if (!selectedOffer || !additionalFieldValues) {
      setOfferAcceptLoading(false);
      setBCAStatus(BCATopUpStatus.FAILED);
      return;
    }

    const abortController = new AbortController();
    const submitResult = await submitBCAOffer(
      abortController,
      Number(amountOptions[fundedAmountIndex]),
      selectedOffer.OfferId,
      additionalFieldValues,
    );

    if (!submitResult.ok) {
      setOfferAcceptLoading(false);
      BCATopUpTealiumService.onSubmitError(submitResult.message || '', submitResult.status || 500);
      setBCAStatus(BCATopUpStatus.FAILED);
      toastMessageServiceSingleton.addError(
        submitResult.result?.Message ?? submitResult.message,
        null,
        '',
        false,
        null,
        true,
      );

      return;
    }

    const result = submitResult.result;
    const address = ['line1', 'line2', 'suburb', 'city', 'province', 'country', 'postcode']
      .map((addressItem) => result.address?.[addressItem])
      .filter((addressValue) => addressValue)
      .join(', ');

    const merchantOfferDetails: IMerchantDetails = {
      merchantName: result.merchantName,
      nameOfOwner: `${additionalFieldValues?.name} ${additionalFieldValues?.surname}`,
      idNumber: additionalFieldValues?.id,
      advanceStart: DateTime.fromJSDate(new Date()).toFormat('dd/LL/yyyy'),
      address: address,
      vatNumber: result.vatRegistrationNumber,
      advanceAmount: amountOptions[fundedAmountIndex],
      totalRepayment: selectedOffer?.TotalRepayment,
      repaymentRate: selectedOffer?.Rate,
    };

    setBcaSubmitStatus(BCASubmitStatus.PREPARING_APPLICATION);
    timeoutManager.restart();
    await Delay(2000);

    const contractBase64 = await getContractPdfBase64(
      merchantOfferDetails,
      merchant,
      selectedOffer,
    );

    const params = {
      amount: Math.abs(selectedOffer.Amount - earlySettlementAmount)
        .toFixed(2)
        .toString(),
      applicationId: result.ApplicationId,
      accountHolderName: result.accountHolderName,
      branchCode: result.branchCode,
      fileBase64: contractBase64,
      bankName: result.bankName,
      accountNumber: result.accountNumber,
      merchantName: result.merchantName,
      merchantEmailId: result.merchantEmailId,
      bankAccountType: result.bankAccountType,
    };

    setBcaSubmitStatus(BCASubmitStatus.SUBMITTING_APPLICATION);
    timeoutManager.restart();
    await Delay(1000);

    const resultAccept = await acceptBCAOffer(params, true);

    if (!resultAccept.ok) {
      toastMessageServiceSingleton.addError(resultAccept?.message, null, '', false, null, true);
      setOfferAcceptLoading(false);
      BCATopUpTealiumService.onSubmitError(resultAccept.message || '', resultAccept.status || 500);
      setBCAStatus(BCATopUpStatus.FAILED);
      return;
    }

    setOfferAcceptLoading(false);
    setBCAStatus(BCATopUpStatus.SUCCESS);
    timeoutManager.clear();
  };

  React.useEffect(() => {
    const getBCAOfferOptions = async () => {
      if (!!offers && Object.keys(offers).length > 0) {
        let amounts = Object.keys(offers.amountTermsMap).sort((a, b) => Number(a) - Number(b));
        setAmountOptions(amounts);
        let i = amounts.findIndex((amount) => {
          return selectedOffer.Amount === parseInt(amount);
        });

        i = i !== -1 ? i : 0;
        setFundedAmountIndex(i);
      }
    };
    getBCAOfferOptions();
  }, [offers]);

  React.useEffect(() => {
    BCATopUpTealiumService.onViewContract();
  }, []);

  return (
    <article className="[&_.NavigationFooter]:sticky [&_.NavigationFooter_.button]:py-3  [&_.NavigationFooter_.button]:px-16 [&_.NavigationFooter_.button]:!w-fit">
      {bcaTopUpStatus !== BCATopUpStatus.NONE ? (
        <DisplayBCASuccessFail
          retries={retries}
          retry={() => {
            setRetries(retries + 1);
            BCATopUpTealiumService.onRetry();
            submitAndAcceptOffer();
          }}
          status={bcaTopUpStatus}
        />
      ) : (
        <>
          <HeaderNav
            title={merchant?.companyName ? `Hi, welcome back ${merchant?.companyName}` : ''}
            screenName="Business Cash Advance Top-up"
          >
            <SubmitModal status={bcaSubmitStatus} show={offerAcceptLoading} />
            <h1 className="my-5 text-xl font-medium text-center px-9 md:my-11 md:text-left md:text-4xl">
              Business Cash Advance Top-up
            </h1>
            <section className="mb-8 md:px-8">
              <div className="bg-grey-800">
                <div className="px-3">
                  <AcceptContractBody
                    earlySettlementAmount={earlySettlementAmount}
                    className="text-xl [&_tr]:flex md:[&_tr]:table-row [&_tr]:flex-col md:[&_.contract-info-values]:!text-right [&_.contract-info-values]:!text-left [&_td]:!py-0 md:[&_td]:!py-3 [&_tr]:!border-b md:[&_tr]:!border-b-0"
                    currentOffer={selectedOffer}
                  />
                </div>
                <div className="flex flex-col items-start justify-between px-6 py-4 text-lg font-bold bg-white md:flex-row md:items-center text-grey-900">
                  <p>Amount disbursed after settlement</p>
                  <p>
                    R{' '}
                    {selectedOffer?.Amount &&
                      offers &&
                      formatCurrencyAmountSimple(
                        Math.abs(selectedOffer.Amount - earlySettlementAmount),
                      )}
                  </p>
                </div>
              </div>
            </section>

            <section className="flex justify-center mb-20 items-centers">
              <div className="flex items-center gap-2 text-base text-grey-300 md:text-lg">
                <img
                  src={accepted ? TickedCheckboxPortal : UntickedCheckboxPortal}
                  alt="toggle-icon"
                  onClick={() => setAccepted(!accepted)}
                />
                <p className="text-center">
                  I have read and agree to the{' '}
                  <span onClick={viewContract} className="text-red-600 underline cursor-pointer">
                    Business Cash Advance Terms and Conditions
                  </span>
                </p>
              </div>
            </section>
          </HeaderNav>
          <NavigationFooter
            onBack={() => history.back()}
            onNext={() => {
              BCATopUpTealiumService.onUIInteraction(
                BCAPageName.VIEW_CONTRACT,
                BCALinkID.NEXT,
                BCAPageUrl.VIEW_CONTRACT,
              );
              submitAndAcceptOffer();
            }}
            nextEnabled={accepted && !!selectedOffer.Amount}
            nextLabel={'Confirm'}
            backLabel="Back"
            backEnabled={true}
          />
        </>
      )}
    </article>
  );
}
